import { useMemo, useState } from "react";
import { ArrowRight, Search, MapPin } from "lucide-react";
import { StepShell, BottomBar, PrimaryButton, Choice } from "./ui";
import { QuestionTitle } from "./Questions";
import { NOT_DECIDED, ANY_AREA } from "../../lib/journey";

const SearchBox = ({ value, onChange, placeholder, testId }) => (
  <div className="relative">
    <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-12 w-full rounded-2xl border border-neutral-200 bg-white pl-11 pr-4 text-[15px] outline-none transition-colors focus:border-[var(--saffron)]"
      data-testid={testId}
    />
  </div>
);

export const CityStep = ({ cities, value, onPick, onBack, progress }) => {
  const featured = cities.filter((c) => c.featured);
  const others = cities.filter((c) => !c.featured);
  const [showOther, setShowOther] = useState(others.some((c) => c.name === value));
  const [q, setQ] = useState("");
  const list = useMemo(() => others.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())), [others, q]);

  return (
    <StepShell stepKey="city" onBack={onBack} progress={progress} testId="question-city">
      <QuestionTitle hint="Pick one — you can refine later.">Which city are you considering?</QuestionTitle>
      <div className="space-y-3">
        {featured.map((c) => (
          <Choice key={c.id} selected={value === c.name} onClick={() => onPick(c.name)} data-testid={`city-featured-${c.name.toLowerCase()}`}>
            {c.name}
          </Choice>
        ))}
        <Choice selected={showOther} onClick={() => setShowOther(!showOther)} data-testid="city-other-toggle">Other cities</Choice>
        {showOther && (
          <div className="rounded-2xl border border-neutral-200 bg-[var(--ivory)] p-3" data-testid="city-other-panel">
            <SearchBox value={q} onChange={setQ} placeholder="Search Indian cities" testId="city-search-input" />
            <ul className="mt-2 max-h-60 overflow-y-auto">
              {list.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => onPick(c.name)}
                    className={`flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 text-left text-[15px] transition-colors hover:bg-white ${value === c.name ? "font-semibold text-[#138808]" : ""}`}
                    data-testid={`city-option-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <MapPin className="h-4 w-4 text-neutral-400" /> {c.name}
                  </button>
                </li>
              ))}
              {!list.length && <li className="px-3 py-3 text-sm text-neutral-500">No city found. Try “Not decided yet”.</li>}
            </ul>
          </div>
        )}
        <Choice selected={value === NOT_DECIDED} onClick={() => onPick(NOT_DECIDED)} data-testid="city-not-decided">{NOT_DECIDED}</Choice>
      </div>
      <div className="pb-6" />
    </StepShell>
  );
};

export const AreaStep = ({ city, areas, value, onPick, onBack, progress }) => {
  const [q, setQ] = useState("");
  const [custom, setCustom] = useState(areas.includes(value) || value === ANY_AREA ? "" : value || "");
  const list = areas.filter((a) => a.toLowerCase().includes(q.toLowerCase()));

  return (
    <StepShell stepKey="area" onBack={onBack} progress={progress} testId="question-area">
      <QuestionTitle hint="Choose a locality, or let us suggest the best fit.">Any preferred area in {city}?</QuestionTitle>
      <div className="space-y-3 pb-4">
        <Choice selected={value === ANY_AREA} onClick={() => onPick(ANY_AREA)} data-testid="area-open-to-suggestions">
          {ANY_AREA}
        </Choice>
        {areas.length > 6 && <SearchBox value={q} onChange={setQ} placeholder={`Search areas in ${city}`} testId="area-search-input" />}
        {list.length > 0 && (
          <div className="flex flex-wrap gap-2.5">
            {list.map((a) => (
              <button
                key={a}
                onClick={() => onPick(a)}
                data-selected={value === a ? "true" : "false"}
                className="choice min-h-[44px] rounded-full border border-neutral-200 px-4 py-2 text-sm font-medium"
                data-testid={`area-option-${a.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {a}
              </button>
            ))}
          </div>
        )}
        <div className="pt-2">
          <label className="text-xs font-medium text-neutral-500" htmlFor="custom-area">Or type an area</label>
          <input
            id="custom-area"
            value={custom}
            maxLength={60}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="e.g. near my parents' home"
            className="mt-1 h-12 w-full rounded-2xl border border-neutral-200 px-4 text-[15px] outline-none focus:border-[var(--saffron)]"
            data-testid="area-custom-input"
          />
        </div>
      </div>
      {custom.trim().length > 1 && (
        <BottomBar>
          <PrimaryButton onClick={() => onPick(custom.trim())} data-testid="area-custom-continue">
            Continue <ArrowRight className="h-5 w-5" />
          </PrimaryButton>
        </BottomBar>
      )}
    </StepShell>
  );
};
